import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useState, ReactNode,
} from 'react';
import type { Phrase, Folder } from './types';
import { SAMPLE_PHRASES, SAMPLE_FOLDERS } from './sampleData';
import type { SearchResult } from './search';
import { createSearchClient } from './searchClient';

const FAVORITES_KEY = 'dp-favorites';
const RECENTS_KEY = 'dp-recents';
const RECENTS_MAX = 12;

type PhrasesContextValue = {
  phrases: Phrase[];
  folders: Folder[];
  /** Ranked full-text search, performed in a worker when a prebuilt index exists. */
  search: (query: string) => Promise<SearchResult[]>;
  loading: boolean;
  favoriteIds: string[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  /** Note ids in most-recently-opened order. */
  recentIds: string[];
  /** Record a note as just opened (moves it to the front of recents). */
  recordRecent: (id: string) => void;
  /** Clear the recently-used list. */
  clearRecents: () => void;
  getPhrase: (id: string) => Phrase | undefined;
};

const PhrasesContext = createContext<PhrasesContextValue | null>(null);

function readList(key: string, fallback: string[]): string[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) return parsed;
  } catch (e) {
    /* ignore */
  }
  return fallback;
}

function writeList(key: string, value: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* ignore */
  }
}

/** Preserve favorites, recents, and deep links created before ICU became a top-level category. */
function currentId(id: string, phrases: Phrase[]): string {
  if (phrases.some((phrase) => phrase.id === id) || !id.startsWith('inpatient-')) return id;
  const icuId = `icu-${id.slice('inpatient-'.length)}`;
  return phrases.some((phrase) => phrase.id === icuId) ? icuId : id;
}

function migrateStoredIds(ids: string[], phrases: Phrase[]): string[] {
  return [...new Set(ids.map((id) => currentId(id, phrases)))];
}

export type PhrasesProviderProps = {
  children: ReactNode;
  /** Override the data source (used by tests and the Phase 5 markdown adapter). */
  source?: { phrases: Phrase[]; folders: Folder[]; searchIndexUrl?: string };
};

export function PhrasesProvider({ children, source }: PhrasesProviderProps) {
  const basePhrases = source?.phrases ?? SAMPLE_PHRASES;
  const folders = source?.folders ?? SAMPLE_FOLDERS;

  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => {
    const seeded = basePhrases.filter((p) => p.favorite).map((p) => p.id);
    return migrateStoredIds(readList(FAVORITES_KEY, seeded), basePhrases);
  });

  const [recentIds, setRecentIds] = useState<string[]>(
    () => migrateStoredIds(readList(RECENTS_KEY, []), basePhrases)
  );

  useEffect(() => writeList(FAVORITES_KEY, favoriteIds), [favoriteIds]);
  useEffect(() => writeList(RECENTS_KEY, recentIds), [recentIds]);

  const recordRecent = useCallback((id: string) => {
    setRecentIds((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, RECENTS_MAX));
  }, []);

  const clearRecents = useCallback(() => setRecentIds([]), []);

  const searchClient = useMemo(
    () => createSearchClient(basePhrases, source?.searchIndexUrl),
    [basePhrases, source?.searchIndexUrl]
  );
  useEffect(() => () => searchClient.dispose(), [searchClient]);
  const search = useCallback((query: string) => searchClient.search(query), [searchClient]);

  const isFavorite = useCallback((id: string) => favoriteIds.includes(id), [favoriteIds]);

  const toggleFavorite = useCallback((id: string) => {
    setFavoriteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  // Project the live favorite state onto the phrase records so consumers
  // can read phrase.favorite directly.
  const decoratedPhrases = useMemo(
    () => basePhrases.map((p) => ({ ...p, favorite: favoriteIds.includes(p.id) })),
    [basePhrases, favoriteIds]
  );

  const value = useMemo<PhrasesContextValue>(
    () => ({
      phrases: decoratedPhrases,
      folders,
      search,
      loading: false,
      favoriteIds,
      isFavorite,
      toggleFavorite,
      recentIds,
      recordRecent,
      clearRecents,
      getPhrase: (id: string) => {
        const resolved = currentId(id, decoratedPhrases);
        return decoratedPhrases.find((p) => p.id === resolved);
      },
    }),
    [decoratedPhrases, folders, search, favoriteIds, isFavorite, toggleFavorite, recentIds, recordRecent, clearRecents]
  );

  return <PhrasesContext.Provider value={value}>{children}</PhrasesContext.Provider>;
}

export function usePhrases(): PhrasesContextValue {
  const ctx = useContext(PhrasesContext);
  if (!ctx) throw new Error('usePhrases must be used within a PhrasesProvider');
  return ctx;
}
